import { userLevelToColor } from '@/functions/user-level-to-color';
import { userLevelToKr } from '@/functions/user-level-to-kr';
import type { User } from '@/types/site';
import type { SortDirection, SortField } from '../hooks/useUserData';

interface UserTableProps {
  users: User[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onEdit: (user: User) => void;
  onDelete: (userId: string) => void;
}

export default function UserTable({
  users,
  sortField,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
}: UserTableProps) {
  const getSortIcon = (field: SortField) => {
    if (sortField === field) {
      return sortDirection === 'asc' ? 'up' : 'down';
    }
    return 'up-down';
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th
              onClick={() => onSort('loginId')}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            >
              <div className="flex items-center space-x-1">
                <span>로그인 아이디</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-${getSortIcon('loginId')}-line text-gray-400`}
                  ></i>
                </div>
              </div>
            </th>
            <th
              onClick={() => onSort('name')}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            >
              <div className="flex items-center space-x-1">
                <span>이름</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-${getSortIcon('name')}-line text-gray-400`}
                  ></i>
                </div>
              </div>
            </th>
            <th
              onClick={() => onSort('mobilePhone')}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            >
              <div className="flex items-center space-x-1">
                <span>휴대폰번호</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-${getSortIcon('mobilePhone')}-line text-gray-400`}
                  ></i>
                </div>
              </div>
            </th>
            <th
              onClick={() => onSort('phone')}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            >
              <div className="flex items-center space-x-1">
                <span>전화번호</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-${getSortIcon('phone')}-line text-gray-400`}
                  ></i>
                </div>
              </div>
            </th>
            <th
              onClick={() => onSort('userLevel')}
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 whitespace-nowrap"
            >
              <div className="flex items-center space-x-1">
                <span>권한등급</span>
                <div className="w-4 h-4 flex items-center justify-center">
                  <i
                    className={`ri-arrow-${getSortIcon('userLevel')}-line text-gray-400`}
                  ></i>
                </div>
              </div>
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              작업
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="font-medium text-gray-900">{user.loginId}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">
                  {user.name}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.mobilePhone}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{user.phone}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${userLevelToColor(user.userLevel)}`}
                >
                  {userLevelToKr(user.userLevel)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(user);
                    }}
                    className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg cursor-pointer"
                    title="정보 수정"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line"></i>
                    </div>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(user.id);
                    }}
                    className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg cursor-pointer"
                    title="회원 삭제"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line"></i>
                    </div>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
